import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, ArrowRight, MessageCircle, Zap,
  Users, BarChart3, Megaphone, Scale, Building2, 
  Stethoscope, GraduationCap, Factory, ShoppingCart
} from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "../../components/Navigation";

const WHATSAPP_LINK = "https://wa.me/33644692901?text=Bonjour%20!%20Je%20souhaite%20une%20formation%20IA%20adaptée%20à%20mon%20secteur.";

const sectors = [
  {
    name: "Ressources Humaines",
    icon: Users,
    color: "blue",
    riskLevel: 65,
    tools: ["HireVue", "Pymetrics", "Eightfold.ai", "ChatGPT Enterprise"],
    useCases: [
      "Tri de CV automatisé (12h → 2 secondes)",
      "Matching candidats intelligent",
      "Prédiction des départs (-75% de turnover)",
      "Rédaction d'offres d'emploi optimisées"
    ],
    roi: "6-18k€/an économisés sur le recrutement"
  },
  {
    name: "Marketing & Communication",
    icon: Megaphone,
    color: "pink",
    riskLevel: 80,
    tools: ["ChatGPT", "Midjourney", "Jasper", "Copy.ai", "Canva AI"],
    useCases: [
      "Génération de contenu x5 plus rapide",
      "Personnalisation des campagnes",
      "Création visuelle automatisée",
      "Analyse prédictive des performances"
    ],
    roi: "76% d'adoption pour la génération de contenu"
  },
  {
    name: "Finance & Comptabilité",
    icon: BarChart3,
    color: "green",
    riskLevel: 78,
    tools: ["ChatGPT Enterprise", "Julius", "Copilot Excel"],
    useCases: [
      "Analyse financière automatisée",
      "Détection de fraude",
      "Prévisions budgétaires",
      "Reporting automatisé"
    ],
    roi: "Accès bases Moody's/MSCI via IA"
  },
  {
    name: "Juridique",
    icon: Scale,
    color: "purple",
    riskLevel: 62,
    tools: ["Claude", "Harvey AI", "Casetext"],
    useCases: [
      "Analyse de contrats en minutes",
      "Recherche jurisprudentielle IA",
      "Rédaction assistée de documents",
      "Due diligence accélérée"
    ],
    roi: "Réduction de 70% du temps de recherche"
  },
  {
    name: "Conseil",
    icon: Building2,
    color: "cyan",
    riskLevel: 72,
    tools: ["Claude", "Notion AI", "Gamma", "Perplexity"],
    useCases: [
      "Livrables 3x plus rapides",
      "Synthèse de documents longs",
      "Présentations auto-générées",
      "Veille concurrentielle automatisée"
    ],
    roi: "+40% de capacité de missions"
  },
  {
    name: "Santé",
    icon: Stethoscope,
    color: "red",
    riskLevel: 68,
    tools: ["Med-PaLM", "Nuance DAX", "PathAI"],
    useCases: [
      "Diagnostic assisté par IA",
      "Transcription médicale automatique",
      "Analyse d'imagerie médicale",
      "Gestion hospitalière optimisée"
    ],
    roi: "L'IA sauve des vies — littéralement"
  },
  {
    name: "Éducation & Formation",
    icon: GraduationCap,
    color: "amber",
    riskLevel: 58,
    tools: ["ChatGPT", "Duolingo AI", "Khan Academy", "Quizlet"],
    useCases: [
      "Parcours personnalisés par élève",
      "Correction automatique intelligente",
      "Tuteurs IA disponibles 24/7",
      "Génération d'exercices sur mesure"
    ],
    roi: "Personnalisation à grande échelle"
  },
  {
    name: "Industrie & Manufacturing",
    icon: Factory,
    color: "slate",
    riskLevel: 70,
    tools: ["Siemens Industrial Copilot", "Azure AI", "Google Cloud AI"],
    useCases: [
      "Maintenance prédictive",
      "Optimisation de production",
      "Contrôle qualité automatisé",
      "Gestion de stock intelligente"
    ],
    roi: "Réduction des arrêts machine de 40%"
  },
  {
    name: "Retail & E-commerce",
    icon: ShoppingCart,
    color: "orange",
    riskLevel: 75,
    tools: ["Shopify Magic", "Dynamic Yield", "Algolia"],
    useCases: [
      "Personnalisation des recommandations",
      "Gestion prédictive des stocks",
      "Pricing dynamique",
      "Service client automatisé"
    ],
    roi: "+30% de conversion avec la personnalisation"
  }
];

const FormationIAMetierPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Formation IA par métier : RH, Marketing, Finance, Juridique | Académie IA</title>
        <meta name="description" content="Formations IA adaptées à chaque secteur : RH, Marketing, Finance, Juridique, Santé, Industrie. Outils et cas d'usage concrets par métier." />
        <meta name="keywords" content="formation IA RH, formation IA marketing, formation IA finance, formation IA juridique, outils IA par secteur" />
        <link rel="canonical" href="https://academie-ia.fr/formation-ia-metier" />
        <meta property="og:title" content="Formation IA par métier" />
        <meta property="og:description" content="Formations IA adaptées à chaque secteur d'activité." />
      </Helmet>

      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium mb-6">
              Formation sur mesure
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6">
              Formation IA adaptée à{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                votre métier
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Chaque secteur a ses outils, ses cas d'usage et ses gains spécifiques. 
              Nous formons vos équipes sur ce qui compte vraiment pour VOTRE activité.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/tunnel"
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl"
              >
                <Zap className="w-5 h-5" />
                Formation personnalisée
              </Link>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-4 bg-green-500 text-white font-bold rounded-xl"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp direct
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sectors.map((sector, i) => (
              <motion.div
                key={sector.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-amber-500/30 transition-all"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-${sector.color}-500/20 flex items-center justify-center`}>
                    <sector.icon className={`w-6 h-6 text-${sector.color}-400`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{sector.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Niveau d'impact IA :</span>
                      <span className={`text-xs font-bold ${sector.riskLevel >= 70 ? 'text-red-400' : 'text-amber-400'}`}>
                        {sector.riskLevel}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">Outils clés :</p>
                  <div className="flex flex-wrap gap-1">
                    {sector.tools.map((tool, j) => (
                      <span key={j} className="text-xs bg-white/10 text-gray-300 px-2 py-1 rounded">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">Cas d'usage :</p>
                  <ul className="space-y-1">
                    {sector.useCases.slice(0, 3).map((useCase, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-1" />
                        {useCase}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`text-sm font-medium text-${sector.color}-400 bg-${sector.color}-500/10 rounded-lg p-2`}>
                  {sector.roi}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sector-Specific */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-900/50 to-[#050505]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pourquoi une formation IA par secteur ?
            </h2>
            <p className="text-xl text-gray-400 mb-12">
              Une formation générique ne produit pas de résultats. Nous travaillons sur VOS cas, avec VOS outils.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Outils ciblés", desc: "Chaque métier a ses outils optimaux. Nous vous formons sur ceux qui comptent." },
                { title: "Cas réels", desc: "Vos collaborateurs travaillent sur leurs propres projets pendant la formation." },
                { title: "ROI immédiat", desc: "Des gains mesurables dès la première semaine post-formation." }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-6"
                >
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Quelle est votre activité ?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Dites-nous votre secteur, nous adaptons notre formation à vos besoins spécifiques.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/formation-ia"
                className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xl font-bold rounded-2xl"
              >
                <Zap className="w-6 h-6" />
                Demander un devis personnalisé
                <ArrowRight className="w-6 h-6" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/10">
        <p className="text-gray-500 text-sm">
          Académie IA © {new Date().getFullYear()} — Formation Intelligence Artificielle
        </p>
      </footer>
    </div>
  );
};

export default FormationIAMetierPage;
